// components/PropertyFilter.js
//
// Gedeelde woningkeuze, in twee vormen:
//  - PropertyFilter (default export): zoekbalk waarvan de dropdown bij een klik
//    alle woningen alfabetisch toont met aan/uit-vinkje, plus knoppen "Alles
//    toevoegen"/"Alles verwijderen" en een pil per gekozen woning.
//    Met single=true kies je er precies 1.
//  - PropertyPicker: lijst met woningkaarten + zoekbalk om 1 woning te openen
//    (Smart lock-tabblad in de app).
// usePropertySelection bewaart de keuze per scherm.
import React, { useState, useMemo, useCallback, useRef, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import API_URL from '../config';
import './PropertyFilter.css';

const readStored = (key) => {
  if (!key) return undefined;
  try {
    const value = JSON.parse(localStorage.getItem(key));
    if (value && value.all) return null; // null = alle woningen
    return Array.isArray(value) ? value : undefined;
  } catch (e) {
    return undefined;
  }
};

const byName = (a, b) => (a.name || '').localeCompare(b.name || '', undefined, { sensitivity: 'base' });

// selected = lijst gekozen woning-id's (leeg = niets gekozen). Standaard zijn
// alle woningen gekozen; bij single=true altijd precies 1 (standaard de eerste
// of "initial"). storageKey leeg = keuze niet onthouden.
export function usePropertySelection(storageKey, properties, single = false, initial = null) {
  const [raw, setRaw] = useState(() => {
    const stored = readStored(storageKey);
    return stored !== undefined ? stored : initial;
  });

  const selected = useMemo(() => {
    const sorted = [...properties].sort(byName);
    const all = properties.map((p) => p.id);
    // Bij 0 of 1 woning verdwijnt het filterbalkje: een oude (bewaarde) keuze
    // mag dan nooit een lege lijst opleveren waar je niet meer uit kan.
    if (properties.length < 2) return all;
    if (raw === null) return single ? (sorted[0] ? [sorted[0].id] : []) : all;
    const valid = new Set(all);
    const cleaned = raw.filter((id) => valid.has(id));
    // Bewaarde keuze bestond alleen nog uit woningen die intussen verwijderd
    // zijn: terug naar alle woningen i.p.v. een leeg scherm.
    if (!single && raw.length > 0 && cleaned.length === 0) return all;
    if (single) return cleaned.length ? [cleaned[0]] : (sorted[0] ? [sorted[0].id] : []);
    return cleaned;
  }, [raw, properties, single]);

  const setSelected = useCallback((ids) => {
    const everything = !single && properties.length > 0 && properties.every((p) => ids.includes(p.id));
    setRaw(everything ? null : ids);
    if (storageKey) {
      try { localStorage.setItem(storageKey, JSON.stringify(everything ? { all: true } : ids)); } catch (e) { /* stil */ }
    }
  }, [storageKey, properties, single]);

  const selectedProperties = useMemo(
    () => (selected.length === properties.length ? properties : properties.filter((p) => selected.includes(p.id))),
    [selected, properties]
  );

  return { selected, setSelected, selectedProperties };
}

// Houdt dezelfde array-referentie vast zolang de inhoud niet verandert, zodat
// kalenders niet bij elke render opnieuw gaan laden.
export function useStableList(list) {
  const ref = useRef({ sig: null, list });
  const sig = JSON.stringify(list);
  if (ref.current.sig !== sig) ref.current = { sig, list };
  return ref.current.list;
}

function SearchField({ value, onChange }) {
  const { t } = useTranslation();
  return (
    <label className="pf-search">
      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><circle cx="11" cy="11" r="7" /><path d="M20 20l-3.5-3.5" /></svg>
      <input
        type="search"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={t('propertyFilter.search')}
        aria-label={t('propertyFilter.search')}
        autoComplete="off"
        data-1p-ignore="true"
        data-lpignore="true"
      />
    </label>
  );
}

function PropertyFilter({ properties, selected, onChange, single = false, showOwner = false }) {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const wrapRef = useRef(null);

  useEffect(() => {
    if (!open) return undefined;
    const close = (e) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false);
    };
    const onKey = (e) => { if (e.key === 'Escape') setOpen(false); };
    document.addEventListener('mousedown', close);
    document.addEventListener('touchstart', close);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', close);
      document.removeEventListener('touchstart', close);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  const sorted = useMemo(() => [...(properties || [])].sort(byName), [properties]);
  if (!properties || properties.length < 2) return null;

  const q = query.trim().toLowerCase();
  const matches = q ? sorted.filter((p) => (p.name || '').toLowerCase().includes(q)) : sorted;
  const selectedSet = new Set(selected);
  const selectedList = sorted.filter((p) => selectedSet.has(p.id));
  const allSelected = selectedList.length === sorted.length;

  const toggle = (id) => {
    if (single) {
      onChange([id]);
      setOpen(false);
      setQuery('');
      return;
    }
    onChange(selectedSet.has(id) ? selected.filter((x) => x !== id) : [...selected, id]);
  };

  const singleName = single ? selectedList[0]?.name : '';

  return (
    <div className="pf" ref={wrapRef}>
      <div className="pf-bar">
        <div className="pf-field">
          <label className={`pf-search ${open ? 'focused' : ''}`}>
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><circle cx="11" cy="11" r="7" /><path d="M20 20l-3.5-3.5" /></svg>
            <input
              type="text"
              value={open || !single ? query : (singleName || '')}
              onChange={(e) => { setQuery(e.target.value); setOpen(true); }}
              onFocus={() => setOpen(true)}
              onClick={() => setOpen(true)}
              placeholder={single && singleName ? singleName : t('propertyFilter.search')}
              aria-label={t('propertyFilter.search')}
              autoComplete="off"
              data-1p-ignore="true"
              data-lpignore="true"
            />
            {query && (
              <button type="button" className="pf-clear" onClick={(e) => { e.preventDefault(); setQuery(''); }} aria-label={t('common.clear', 'Wissen')}>✕</button>
            )}
            <button
              type="button"
              className={`pf-caret ${open ? 'open' : ''}`}
              onClick={(e) => { e.preventDefault(); setOpen((v) => !v); }}
              aria-label={t('propertyFilter.label')}
              tabIndex={-1}
            >
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"><path d="M6 9l6 6 6-6" /></svg>
            </button>
          </label>

          {open && (
            <div className="pf-dropdown" role="listbox" aria-multiselectable={!single}>
              {!single && (
                <div className="pf-dropdown-head">
                  <span className="pf-count">{t('propertyFilter.count', { count: selectedList.length, total: sorted.length })}</span>
                  <span className="pf-head-actions">
                    <button type="button" onClick={() => onChange(sorted.map((p) => p.id))} disabled={allSelected}>{t('propertyFilter.addAll')}</button>
                    <button type="button" onClick={() => onChange([])} disabled={selectedList.length === 0}>{t('propertyFilter.removeAll')}</button>
                  </span>
                </div>
              )}
              <div className="pf-dropdown-list">
                {matches.length === 0 && <div className="pf-none">{t('propertyFilter.noResults')}</div>}
                {matches.map((p) => {
                  const on = selectedSet.has(p.id);
                  return (
                    <button
                      key={p.id}
                      type="button"
                      role="option"
                      aria-selected={on}
                      className={`pf-item ${on ? 'on' : ''}`}
                      onClick={() => toggle(p.id)}
                    >
                      <span className={`pf-check ${single ? 'radio' : ''}`}>{on ? '✓' : ''}</span>
                      <span className="pf-item-name">{p.name}</span>
                      {showOwner && p.owner_name && <span className="pf-item-owner">{p.owner_name}</span>}
                    </button>
                  );
                })}
              </div>
            </div>
          )}
        </div>

        {!single && (
          <div className="pf-bulk">
            <button type="button" onClick={() => onChange(sorted.map((p) => p.id))} disabled={allSelected}>{t('propertyFilter.addAll')}</button>
            <button type="button" onClick={() => onChange([])} disabled={selectedList.length === 0}>{t('propertyFilter.removeAll')}</button>
          </div>
        )}
      </div>

      {!single && (
        <div className="pf-chips">
          {selectedList.length === 0 && <span className="pf-summary">{t('propertyFilter.noneSelected')}</span>}
          {allSelected && <span className="pf-summary">{t('propertyFilter.allSelected', { total: sorted.length })}</span>}
          {!allSelected && selectedList.map((p) => (
            <button key={p.id} type="button" className="pf-chip" onClick={() => toggle(p.id)} title={t('propertyFilter.remove')}>
              <span className="pf-chip-name">{p.name}</span>
              <span className="pf-chip-x">✕</span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

export function PropertyPicker({ properties, onPick, onEdit, title, grid = false }) {
  const { t } = useTranslation();
  const [query, setQuery] = useState('');
  const q = query.trim().toLowerCase();
  const sorted = useMemo(() => [...properties].sort(byName), [properties]);
  const visible = q
    ? sorted.filter((p) => `${p.name || ''} ${p.address || ''}`.toLowerCase().includes(q))
    : sorted;

  return (
    <div className={`pp ${grid ? 'pp-grid' : ''}`}>
      {title && <h2 className="pp-title">{title}</h2>}
      {properties.length > 1 && <SearchField value={query} onChange={setQuery} />}
      <ul className="pp-list">
        {visible.map((p) => (
          <li key={p.id}>
            <div className="pp-card" role="button" tabIndex={0} onClick={() => onPick(p)} onKeyDown={(e) => { if (e.key === 'Enter') onPick(p); }}>
              {p.photoUrl ? (
                <img src={/^(https?:|data:|\/demo-photos)/.test(p.photoUrl) ? p.photoUrl : `${API_URL}${p.photoUrl}`} alt="" className="pp-photo" />
              ) : (
                <span className="pp-photo pp-photo-empty">
                  <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round"><path d="M3 11L12 3L21 11M5 9.2V20H9.5V14H14.5V20H19V9.2" /></svg>
                </span>
              )}
              <span className="pp-text">
                <span className="pp-name">{p.name}</span>
                {p.address && <span className="pp-address">{p.address}</span>}
              </span>
              {onEdit && (
                <button
                  type="button"
                  className="pp-edit"
                  aria-label={t('common.edit')}
                  onClick={(e) => { e.stopPropagation(); onEdit(p); }}
                >
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round"><path d="M17 3L21 7L7 21H3V17L17 3Z" /></svg>
                </button>
              )}
              <svg className="pp-chevron" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><path d="M9 6l6 6-6 6" /></svg>
            </div>
          </li>
        ))}
        {visible.length === 0 && <li className="pp-none">{t('propertyFilter.noResults')}</li>}
      </ul>
    </div>
  );
}

export default PropertyFilter;
export { PropertyFilter };
